// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';

import { useContext, useState } from 'react';
import { useRouter } from 'next/router';

import { ViewerItem } from 'presentations/api/schema/user';
import { apiPath, dashboardPath } from 'presentations/routes/path';
import { useProjectForm } from 'presentations/views/components/organisms/ProjectForm/hooks';
import { useRequest } from 'presentations/views/hooks/request';

import { ProjectItem } from 'presentations/api/schema/project';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { destroy } from 'infras/rest_client';
import { getErrorMessage } from 'presentations/views/helpers/error';
import { useMemberListDialog } from '../dialog/MemberListDialog/hooks';
import { useMemberInviteDialog } from '../dialog/MemberInviteDialog/hooks';

interface Props {
  viewer: ViewerItem;
  project?: ProjectItem;
}

export const useProjectHeader = (props: Props) => {
  const t = translateInComponent(translations);
  const { dispatch: navigationDispatch } = useContext(DashboardPortalContext);
  const router = useRouter();
  const projectUid = router.query.project ? String(router.query.project) : null;

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [deleteProject, setDeleteProject] = useState<ProjectItem | null>(null);

  const projectRequest = useRequest(
    projectUid === null
      ? null
      : apiPath(`/v1/teams/${props.viewer.team!.uid}/projects/${projectUid}`),
  );
  const project: ProjectItem | undefined | null =
    projectRequest.data || props.project;
  const isProjectLoaded = !!project;

  const projectForm = useProjectForm({
    teamUid: props.viewer.team!.uid,
    project: project || undefined,
    onRequested: (project) => {
      if ('users' in project) {
        projectRequest.mutate({ ...projectRequest, data: project });
      }
    },
  });
  const memberListDialog = useMemberListDialog({
    viewer: props.viewer,
    projectUid: projectUid || '',
    users: project?.users || [],
    onDeleted: (users) => {
      projectRequest.mutate({ ...projectRequest, data: { ...project, users } });
    },
  });
  const memberInviteDialog = useMemberInviteDialog({
    viewer: props.viewer,
    projectUid: projectUid || '',
    project: project ?? undefined,
    onRequested: (newUsers) => {
      projectRequest.mutate({
        ...projectRequest,
        data: { ...project, users: [...newUsers, ...(project?.users || [])] },
      });
    },
  });
  const onClickSettings = () => {
    projectForm.actions.setIsOpen(true);
  };
  const onClickMemberList = () => {
    memberListDialog.actions.setIsOpen(true);
  };
  const onClickInvite = () => {
    memberInviteDialog.actions.setIsOpen(true);
  };

  const onCloseDeleteProject = () => {
    setDeleteProject(null);
  };

  const confirmDeleteProject = () => {
    if (project) {
      setDeleteProject(project);
    }
  };

  const onDeleteProject = async () => {
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/projects/${projectUid}/delete`,
      ),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(getErrorMessage(result)));
      return;
    }
    setDeleteProject(null);
    setIsConfirmLoading(false);
    navigationDispatch(setSuccessMessage(t('Project deleted')));
    router.push(dashboardPath());
  };

  const isDeletable = project ? !Boolean(project.isDefaultAdd) : false;
  const menuOptions = [];
  if (isDeletable) {
    menuOptions.push({
      name: t('Delete'),
      icon: 'delete',
      onClick: () => {
        setDeleteProject(project!);
      },
    });
  }

  return {
    state: {
      project,
      isProjectLoaded,
      projectForm: projectForm.state,
      memberListDialog: memberListDialog.state,
      memberInviteDialog: memberInviteDialog.state,
      isDeletable,
      isConfirmLoading,
      deleteProject,
      menuOptions,
    },
    actions: {
      onClickSettings,
      onClickMemberList,
      onClickInvite,
      onDeleteProject,
      onCloseDeleteProject,
      confirmDeleteProject,
      projectForm: projectForm.actions,
      memberListDialog: memberListDialog.actions,
      memberInviteDialog: memberInviteDialog.actions,
    },
  };
};
